import React, { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Card, Chip, Grid, Stack, Typography } from '@mui/material';
import Dropdown from 'components/Dropdown';
import ScrollableTable from 'components/ScrollableTable';
import DrawerMenu from 'components/DrawerMenu';
import DrawerContent from './DrawerContent';
import ManagerAccountSummaryChart from './ManagerAccountSummaryChart';
import ManagerWisePerformance from './ManagerWisePerformance';
import ExecutiveSummary, { ExecutiveSummaryData } from './ExecutiveSummary';
import CategoryPortfolioCard, { CategoryKey, CATEGORY_PORTFOLIO_CONFIG } from './CategoryPortfolioCard';
import RankBadge from './RankBadge';
import { formatNumber } from '../utils/formatters';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { PERIOD_TYPES } from 'utils/constants/data';

const COMPANY_PORTFOLIO_LIMIT = 5;

interface PortfolioIntelligenceProps {
  portfolioExecutive: string | number;
  setPortfolioExecutive: (value: string | number) => void;
  executivePortfolio: any;
  extractedCompanyPortfolio: any[];
  managerAccountSummary: any[];
  filterOptions: {
    managers: any[];
    categories: any[];
  };
  onCompanyClick: (accountId: number) => void;
  onCategoryClick: (category: 'A' | 'B' | 'C' | 'O', companies: any[], managerName: string) => void;
  managerWisePerformance: any[];
  managerWisePerformanceLoading: boolean;
  managerWisePeriodType: string;
  managerWisePeriod: string | number;
  managerWisePeriods: any[];
  onManagerWisePeriodTypeChange: (value: string) => void;
  onManagerWisePeriodChange: (value: string | number) => void;
}

const PortfolioIntelligence: FC<PortfolioIntelligenceProps> = ({
  portfolioExecutive,
  setPortfolioExecutive,
  executivePortfolio,
  extractedCompanyPortfolio,
  managerAccountSummary,
  filterOptions,
  onCompanyClick,
  onCategoryClick,
  managerWisePerformance,
  managerWisePerformanceLoading,
  managerWisePeriodType,
  managerWisePeriod,
  managerWisePeriods,
  onManagerWisePeriodTypeChange,
  onManagerWisePeriodChange,
}) => {
  const [executiveSummary, setExecutiveSummary] = useState<ExecutiveSummaryData | null>(null);
  const [executiveSummaryLoading, setExecutiveSummaryLoading] = useState(false);
  const [companyViewAllOpen, setCompanyViewAllOpen] = useState(false);

  // Order managers by revenue (desc) so Manager Performance and Manager Account Summary stay in sync.
  const managerSortOrder = useMemo(() => {
    const revenueOf = (m: any) =>
      Number(m?.summary?.roomRevenue?.achieved ?? 0) + Number(m?.summary?.miceRevenue?.achieved ?? 0);
    const order = new Map<string, number>();
    (Array.isArray(managerWisePerformance) ? [...managerWisePerformance] : [])
      .sort((a, b) => revenueOf(b) - revenueOf(a))
      .forEach((m, index) => {
        const name = String(m?.userName ?? '').trim().toLowerCase();
        if (name) order.set(name, index);
      });
    return order;
  }, [managerWisePerformance]);

  const fetchExecutiveSummary = useCallback(async (userId: string | number, period: string | number) => {
    if (!userId || !period) {
      setExecutiveSummary(null);
      return;
    }
    const periodId = Number(period);
    if (isNaN(periodId)) {
      setExecutiveSummary(null);
      return;
    }
    try {
      setExecutiveSummaryLoading(true);
      const { data } = await axiosInstance.get(URLS.getExecutivePortfolioSummary(String(userId), periodId));
      setExecutiveSummary(data);
    } catch {
      setExecutiveSummary(null);
    } finally {
      setExecutiveSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (portfolioExecutive && managerWisePeriod) {
      fetchExecutiveSummary(portfolioExecutive, managerWisePeriod);
    } else {
      setExecutiveSummary(null);
    }
  }, [portfolioExecutive, managerWisePeriod, fetchExecutiveSummary]);

  // Helper to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const categoryPortfolioCards = useMemo(() => {
    const summaries = executivePortfolio?.categorySummaries ?? {};
    const categories: CategoryKey[] = ['A', 'B', 'C', 'O'];

    return categories.map(category => {
      const catSummary = summaries?.[category] ?? {};
      const companies: any[] = Array.isArray(catSummary?.companies)
        ? catSummary.companies
        : catSummary?.companies
        ? Object.values(catSummary.companies)
        : [];

      const revenue = companies.reduce(
        (sum, company) =>
          sum + Number(company?.fitRoomPotential ?? 0) + Number(company?.miceEventPotential ?? 0),
        0,
      );
      const roomNights = companies.reduce((sum, company) => sum + Number(company?.fitRoomNights ?? 0), 0);
      const miceEvents = companies.reduce((sum, company) => sum + Number(company?.miceEventCount ?? 0), 0);
      const companyCount = Number(catSummary?.companyCount ?? companies.length);
      const avgRpd =
        companyCount > 0
          ? Number(catSummary?.avgRPD ?? catSummary?.avgRpd ?? 0) / companyCount
          : 0;

      return {
        category,
        companyCount,
        revenue,
        roomNights,
        miceEvents,
        avgRpd,
        companies,
      };
    });
  }, [executivePortfolio]);

  const renderCompanyTable = (rows: any[]) => (
    <ScrollableTable
      columns={[
        { label: '#', key: 'rank', align: 'center' },
        { label: 'Company Name', key: 'companyName', align: 'left' },
        { label: 'Category', key: 'category', align: 'center' },
        { label: 'Industry', key: 'industry', align: 'left' },
        { label: 'Managers', key: 'managers', align: 'left' },
        { label: 'MICE Potential', key: 'micePotential', align: 'right' },
        { label: 'Room Potential', key: 'roomPotential', align: 'right' },
      ]}
      data={rows}
      maxHeight={rows.length > COMPANY_PORTFOLIO_LIMIT ? 520 : 350}
      onRowClick={row => onCompanyClick(Number(row?.accountId ?? row?.id))}
      renderCell={(column, row) => {
        const categoryKey = String(row?.category ?? 'O').charAt(0).toUpperCase() as CategoryKey;
        const categoryMeta = CATEGORY_PORTFOLIO_CONFIG[categoryKey] ?? CATEGORY_PORTFOLIO_CONFIG.O;

        const cellRenderers: Record<string, () => React.ReactNode> = {
          rank: () => (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <RankBadge rank={Number(row?.rank ?? 0)} />
            </Box>
          ),
          companyName: () => (
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {row?.companyName ?? row?.name ?? 'Company'}
            </Typography>
          ),
          category: () => (
            <Chip
              label={categoryKey}
              size="small"
              sx={{
                height: 22,
                minWidth: 28,
                fontWeight: 700,
                fontSize: '0.72rem',
                color: categoryMeta.color,
                backgroundColor: `${categoryMeta.color}1A`,
              }}
            />
          ),
          industry: () => (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {row?.industryType ?? row?.industry ?? '—'}
            </Typography>
          ),
          managers: () => (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {Array.isArray(row?.managedBy) ? row.managedBy.join(', ') : row?.managedBy ?? '—'}
            </Typography>
          ),
          micePotential: () => (
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {formatNumber(Number(row?.micePotential ?? row?.miceEventPotential ?? 0))}
            </Typography>
          ),
          roomPotential: () => (
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {formatNumber(Number(row?.roomNightPotential ?? row?.fitRoomPotential ?? 0))}
            </Typography>
          ),
        };
        const renderer = column.key ? cellRenderers[column.key] : null;
        return renderer ? renderer() : null;
      }}
      getRowKey={(row, index) => row?.accountId ?? row?.id ?? index}
      emptyMessage="No companies found"
    />
  );

  const topCompanies = extractedCompanyPortfolio.slice(0, COMPANY_PORTFOLIO_LIMIT);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Common sticky filter bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          backgroundColor: '#fff',
          borderRadius: 2,
          border: '1px solid #E5E7EB',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          px: 2,
          py: 1.5,
        }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap' }}>
          <Dropdown
            label="Period Type"
            value={managerWisePeriodType}
            options={PERIOD_TYPES}
            onChange={(value: string | number) => onManagerWisePeriodTypeChange(String(value))}
            minWidth={150}
          />
          <Dropdown
            label="Period"
            value={managerWisePeriod}
            options={managerWisePeriods.map((p: any) => ({
              id: p.value ?? p.id,
              name: p.label ?? p.name ?? String(p.value ?? p.id),
            }))}
            onChange={(value: string | number) => onManagerWisePeriodChange(value)}
            minWidth={200}
          />
          <Dropdown
            label="Executive"
            value={portfolioExecutive}
            options={filterOptions.managers.map((m: any) => ({
              id: m.id,
              name: m.name || m.label || String(m.id),
            }))}
            onChange={value => setPortfolioExecutive(value)}
            minWidth={220}
          />
        </Stack>
      </Box>

      {/* Executive Summary */}
      {portfolioExecutive && (
        <ExecutiveSummary summary={executiveSummary} loading={executiveSummaryLoading} />
      )}

      {/* Category Portfolio */}
      {portfolioExecutive && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              Category Portfolio
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              Performance overview by company category.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {categoryPortfolioCards.map(card => (
              <Grid item xs={12} sm={6} md={3} key={card.category}>
                <CategoryPortfolioCard
                  category={card.category}
                  companyCount={card.companyCount}
                  revenue={card.revenue}
                  roomNights={card.roomNights}
                  miceEvents={card.miceEvents}
                  avgRpd={card.avgRpd}
                  onViewCompanies={
                    card.companies.length
                      ? () =>
                          onCategoryClick(
                            card.category,
                            card.companies,
                            executivePortfolio?.executiveName ?? 'Executive',
                          )
                      : undefined
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Manager-wise Performance, Manager Account Summary and Company Portfolio */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7.2}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}>
            <ManagerWisePerformance
              managerWisePerformance={managerWisePerformance}
              managerWisePerformanceLoading={managerWisePerformanceLoading}
              managerWisePeriodType={managerWisePeriodType}
              managerWisePeriod={managerWisePeriod}
              getInitials={getInitials}
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={4.8}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Manager account summary
                </Typography>
                <Chip
                  label={`${managerAccountSummary.length} managers`}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
            <ManagerAccountSummaryChart
              data={managerAccountSummary}
              formatNumber={formatNumber}
              onCategoryClick={onCategoryClick}
              managerOrder={managerSortOrder}
              emptyMessage="No account summary found"
            />
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Company portfolio
                </Typography>
                {extractedCompanyPortfolio.length > 0 && (
                  <Chip
                    label={`${extractedCompanyPortfolio.length} companies`}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Box>
            </Box>
            {extractedCompanyPortfolio.length ? (
              <>
                <Box sx={{ flex: 1, minHeight: 0 }}>{renderCompanyTable(topCompanies)}</Box>
                {extractedCompanyPortfolio.length > COMPANY_PORTFOLIO_LIMIT && (
                  <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCompanyViewAllOpen(true)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}>
                      View all companies ({extractedCompanyPortfolio.length})
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No companies found
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      <DrawerMenu
        width="80%"
        open={companyViewAllOpen}
        handleClose={() => setCompanyViewAllOpen(false)}
        closeButton
        title="Company portfolio — All companies"
        dropBack>
        <DrawerContent padding={3}>{renderCompanyTable(extractedCompanyPortfolio)}</DrawerContent>
      </DrawerMenu>
    </Box>
  );
};

export default PortfolioIntelligence;
